import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  getScreenTree,
  getAllAccessIds,
  getAccessLabel,
  deriveAccessIdsFromPermissions,
  expandAccessToPermissions,
} from '../config/navigation';

export default function Profiles({ api }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [newProfile, setNewProfile] = useState({
    name: '',
    description: '',
    permissions: [] // durante a edição, guarda os accessIds selecionados
  });
  const [expandedGroups, setExpandedGroups] = useState({});

  // Fonte única de verdade: árvore de telas derivada do menu do sistema.
  const screenTree = getScreenTree();
  const allAccessIds = getAllAccessIds();

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/profiles');
      setProfiles(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para gerenciar perfis');
      } else {
        toast.error('Erro ao carregar perfis');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProfile = async () => {
    if (!newProfile.name.trim()) {
      toast.error('Nome do perfil é obrigatório');
      return;
    }

    try {
      await api.post('/profiles', {
        name: newProfile.name,
        description: newProfile.description,
        permissions: expandAccessToPermissions(newProfile.permissions, []),
      });
      toast.success('Perfil criado com sucesso!');
      setShowAddModal(false);
      setNewProfile({
        name: '',
        description: '',
        permissions: []
      });
      fetchProfiles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar perfil';
      toast.error(message);
    }
  };

  const handleUpdateProfile = async () => {
    if (!selectedProfile) return;

    try {
      await api.put(`/profiles/${selectedProfile.id}`, {
        name: selectedProfile.name,
        description: selectedProfile.description,
        permissions: expandAccessToPermissions(
          selectedProfile.permissions,
          selectedProfile._original || []
        ),
      });
      toast.success('Perfil atualizado com sucesso!');
      setShowEditModal(false);
      setSelectedProfile(null);
      fetchProfiles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar perfil';
      toast.error(message);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Tem certeza que deseja excluir este perfil? Esta ação não pode ser desfeita.')) return;

    try {
      await api.delete(`/profiles/${profileId}`);
      toast.success('Perfil excluído com sucesso!');
      fetchProfiles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir perfil';
      toast.error(message);
    }
  };

  // Atualiza os accessIds selecionados no formulário aberto (criar ou editar).
  const setFormAccess = (updater, isEdit) => {
    if (isEdit) {
      setSelectedProfile(prev => (prev ? { ...prev, permissions: updater(prev.permissions) } : prev));
    } else {
      setNewProfile(prev => ({ ...prev, permissions: updater(prev.permissions) }));
    }
  };

  const toggleAccess = (accessId, isEdit = false) => setFormAccess(
    (perms) => (perms.includes(accessId) ? perms.filter(p => p !== accessId) : [...perms, accessId]),
    isEdit
  );

  // Marca/desmarca todos os submenus de um menu principal.
  const toggleGroup = (childIds, select, isEdit = false) => setFormAccess((perms) => {
    const set = new Set(perms);
    childIds.forEach(id => (select ? set.add(id) : set.delete(id)));
    return Array.from(set);
  }, isEdit);

  const toggleAll = (select, isEdit = false) => setFormAccess(
    () => (select ? [...allAccessIds] : []),
    isEdit
  );

  const isGroupOpen = (id) => expandedGroups[id] !== false; // aberto por padrão
  const Chevron = ({ open }) => (
    <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
    </svg>
  );

  // Renderiza a árvore de telas (menu principal expansível → submenus).
  const renderPermissionSelector = (selected, isEdit = false) => {
    const allSelected = allAccessIds.length > 0 && allAccessIds.every(id => selected.includes(id));
    const uid = (id) => `${isEdit ? 'edit-' : 'new-'}${id}`;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">Permissões de Acesso (Telas)</Label>
          <button
            type="button"
            onClick={() => toggleAll(!allSelected, isEdit)}
            className="text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            {allSelected ? 'Desmarcar todas' : 'Selecionar todas'}
          </button>
        </div>
        <p className="text-xs text-slate-500 -mt-1">
          Escolha os menus e telas que este perfil poderá acessar. Os nomes são os mesmos do menu lateral.
        </p>

        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {screenTree.map((node) => {
            if (node.type === 'item') {
              const checked = selected.includes(node.accessId);
              return (
                <div key={node.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50">
                  <Checkbox
                    id={uid(node.accessId)}
                    checked={checked}
                    onCheckedChange={() => toggleAccess(node.accessId, isEdit)}
                    data-testid={`screen-${node.id}`}
                  />
                  <Label htmlFor={uid(node.accessId)} className="font-medium cursor-pointer">
                    {node.label}
                  </Label>
                </div>
              );
            }

            // Grupo (menu principal com submenus)
            const childIds = node.children.map(c => c.accessId);
            const allChild = childIds.every(id => selected.includes(id));
            const someChild = childIds.some(id => selected.includes(id));
            const open = isGroupOpen(node.id);
            return (
              <div key={node.id} className="border rounded-lg overflow-hidden">
                <div className="flex items-center space-x-3 p-3 bg-slate-50">
                  <Checkbox
                    id={uid(`group-${node.id}`)}
                    checked={allChild}
                    onCheckedChange={() => toggleGroup(childIds, !allChild, isEdit)}
                    data-testid={`screen-group-${node.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [node.id]: !open }))}
                    className="flex-1 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-slate-800">
                      {node.label}
                      {someChild && !allChild && (
                        <span className="ml-2 text-xs font-normal text-blue-600">(parcial)</span>
                      )}
                    </span>
                    <Chevron open={open} />
                  </button>
                </div>
                {open && (
                  <div className="divide-y">
                    {node.children.map((child) => {
                      const checked = selected.includes(child.accessId);
                      return (
                        <div key={child.id} className="flex items-center space-x-3 py-2.5 pl-10 pr-3 hover:bg-slate-50">
                          <Checkbox
                            id={uid(child.accessId)}
                            checked={checked}
                            onCheckedChange={() => toggleAccess(child.accessId, isEdit)}
                            data-testid={`screen-${child.id}`}
                          />
                          <Label htmlFor={uid(child.accessId)} className="text-sm cursor-pointer">
                            {child.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6">
              <div className="h-4 bg-slate-200 rounded w-1/4 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="profiles-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Gestão de Perfis</h1>
          <p className="text-slate-600 mt-1">Configure perfis e permissões de acesso</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-profile-button">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Novo Perfil
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Criar Novo Perfil</DialogTitle>
              <DialogDescription>Configure um novo perfil de acesso com permissões específicas</DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Nome do Perfil *</Label>
                  <Input
                    id="profile-name"
                    data-testid="profile-name-input"
                    value={newProfile.name}
                    onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                    placeholder="Ex: Vendedor, Gerente, Suporte..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-description">Descrição</Label>
                  <Textarea
                    id="profile-description"
                    data-testid="profile-description-input"
                    value={newProfile.description}
                    onChange={(e) => setNewProfile({...newProfile, description: e.target.value})}
                    placeholder="Descreva o propósito deste perfil..."
                    rows={3}
                  />
                </div>
              </div>

              {renderPermissionSelector(newProfile.permissions, false)}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddProfile} className="btn-primary" data-testid="save-profile-button">
                Criar Perfil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Profiles List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="profiles-list">
        {profiles.length === 0 ? (
          <div className="col-span-full">
            <Card className="glass border-white/20">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum perfil encontrado</h3>
                <p className="text-slate-500">Crie seu primeiro perfil de acesso</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          profiles.map((profile) => (
            <Card key={profile.id} className="glass border-white/20 card-hover" data-testid={`profile-card-${profile.id}`}>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-xl text-slate-900 break-words">{profile.name}</CardTitle>
                    {profile.description && (
                      <CardDescription className="mt-2">{profile.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedProfile({
                          ...profile,
                          _original: profile.permissions,
                          permissions: deriveAccessIdsFromPermissions(profile.permissions),
                        });
                        setExpandedGroups({});
                        setShowEditModal(true);
                      }}
                      className="bg-white/60"
                      data-testid={`edit-profile-${profile.id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteProfile(profile.id)}
                      data-testid={`delete-profile-${profile.id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-4">
                  {(() => {
                    const screens = deriveAccessIdsFromPermissions(profile.permissions);
                    return (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          Telas liberadas ({screens.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {screens.length === 0 ? (
                            <Badge variant="outline" className="text-slate-500">
                              Nenhuma tela
                            </Badge>
                          ) : (
                            screens.map((accessId) => (
                              <Badge key={accessId} variant="secondary" className="text-xs">
                                {getAccessLabel(accessId)}
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="text-xs text-slate-500 border-t pt-3">
                    Criado em {new Date(profile.created_at).toLocaleDateString('pt-BR')}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Perfil</DialogTitle>
            <DialogDescription>Atualize as informações e permissões do perfil</DialogDescription>
          </DialogHeader>

          {selectedProfile && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-profile-name">Nome do Perfil</Label>
                  <Input
                    id="edit-profile-name"
                    value={selectedProfile.name}
                    onChange={(e) => setSelectedProfile({...selectedProfile, name: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-profile-description">Descrição</Label>
                  <Textarea
                    id="edit-profile-description"
                    value={selectedProfile.description}
                    onChange={(e) => setSelectedProfile({...selectedProfile, description: e.target.value})}
                    rows={3}
                  />
                </div>
              </div>

              {renderPermissionSelector(selectedProfile.permissions, true)}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateProfile} className="btn-primary">
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
